import { FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { HeatSeries, MaterialRead } from "src/store/api/generatedApi";
import { ToggleSelector } from "../../ToggleSelector";
import {
  HeatDropTimeline,
  DropBreakdown,
  DropGrouping,
} from "./HeatDropTimeline";
import { HeatDropSummaryTable } from "./HeatDropSummaryTable";
import { useEffect, useMemo, useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import {
  ProvideScrapEyeMetadata,
  useScrapEyeMetadata,
} from "contexts/scrapEyeMetadata";
import dayjs from "dayjs";
import { useTenant } from "hooks/settings";
import { useScaleWeights } from "hooks/useScaleWeights";
import { useHeatsSelection } from "../context/HeatsSelectionContext";

interface Props {
  heatData: HeatSeries;
  materials: MaterialRead[];
}

export const HeatTimelineTab = ({ materials, heatData }: Props) => {
  return (
    <ProvideScrapEyeMetadata>
      <HeatTimelineTabInner materials={materials} heatData={heatData} />
    </ProvideScrapEyeMetadata>
  );
};

const HeatTimelineTabInner = ({ materials, heatData }: Props) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { extra_data, scale_weight_source, drop_areas } = useScrapEyeMetadata();
  const hasMassData = extra_data.includes("mass_data");
  const [dropBreakdown, setDropBreakdown] = useState<DropBreakdown>(
    hasMassData ? "mass" : "lifts"
  );
  const [showScaleWeight, setShowScaleWeight] = useState(false);
  const { dropGrouping, setDropGrouping } = useHeatsSelection();

  const relevantScaleAreas = useMemo(() => {
    if (scale_weight_source === "crane") {
      // Mass series keyed by crane location — match against location_name on drops.
      return new Set(
        heatData.drops
          .map((d) => d.location_name)
          .filter((loc): loc is string => loc !== null)
      );
    }
    // "bucket" — mass series keyed by consumption area, match to_area.
    return new Set(heatData.drops.map((d) => d.to_area));
  }, [heatData.drops, scale_weight_source]);

  const materialNames = materials.map((material) => material.name);
  const loadingStart = heatData.drops.reduce(
    (acc, drop) => {
      if (dayjs(drop.drop_timestamp).isBefore(dayjs(acc))) {
        return drop.drop_timestamp;
      }
      return acc;
    },
    heatData.loading_end_timestamp ??
      heatData.loading_start_timestamp ??
      heatData.start_timestamp
  );
  const loadingEnd = heatData.drops.reduce((acc, drop) => {
    if (dayjs(drop.drop_timestamp).isAfter(dayjs(acc))) {
      return drop.drop_timestamp;
    }
    return acc;
  }, heatData.loading_start_timestamp ?? heatData.start_timestamp);

  const scaleWeights = useScaleWeights({
    tenantName,
    start: dayjs(loadingStart),
    end: dayjs(loadingEnd),
    downsample: 5,
    skip: !hasMassData || !showScaleWeight || dropBreakdown !== "mass",
  });

  // Determine which grouping options are meaningful for this heat's drops.
  const availableGroupings = useMemo((): Partial<
    Record<DropGrouping, string>
  > => {
    const craneNames = new Set(
      heatData.drops
        .map((d) => d.location_name)
        .filter((n): n is string => n !== null)
    );
    const areaNames = new Set(
      heatData.drops
        .map(
          (d) =>
            d.consumption_area ??
            (drop_areas.includes(d.to_area)
              ? d.to_area
              : drop_areas.includes(d.from_area)
                ? d.from_area
                : null)
        )
        .filter((a): a is string => a !== null)
    );

    const result: Partial<Record<DropGrouping, string>> = {
      combined: "combined",
    };
    if (craneNames.size > 1) result.byCrane = "byCrane";
    if (areaNames.size > 1) result.byConsumptionArea = "byConsumptionArea";
    return result;
  }, [heatData.drops, drop_areas]);

  // If the stored grouping is no longer available for this heat, fall back to combined.
  useEffect(() => {
    if (!(dropGrouping in availableGroupings)) {
      setDropGrouping("combined");
    }
  }, [dropGrouping, availableGroupings, setDropGrouping]);

  // Show the scale weight toggle only when the current grouping aligns with the
  // tenant's weight source: crane weights → "byCrane"; bucket weights → "byConsumptionArea";
  // always visible in combined.
  const scaleWeightMatchesGrouping =
    dropGrouping === "combined" ||
    (dropGrouping === "byCrane" && scale_weight_source === "crane") ||
    (dropGrouping === "byConsumptionArea" && scale_weight_source === "bucket");
  const showScaleWeightToggle =
    hasMassData && dropBreakdown === "mass" && scaleWeightMatchesGrouping;

  return (
    <>
      <Stack direction="row" gap={3} alignItems="center" sx={{ mt: 1, px: 3 }}>
        <Typography variant="body1">{`${t("viewAs")}: `}</Typography>
        <ToggleSelector<DropBreakdown>
          selectedValue={dropBreakdown}
          setSelectedValue={setDropBreakdown}
          availableValueLabels={{ lifts: "totalLifts", mass: "mixMass" }}
        />
        <ToggleSelector<DropGrouping>
          selectedValue={dropGrouping}
          setSelectedValue={setDropGrouping}
          availableValueLabels={
            availableGroupings as Record<DropGrouping, string>
          }
        />
        {showScaleWeightToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={showScaleWeight}
                onChange={(e) => {
                  setShowScaleWeight(e.target.checked);
                }}
              />
            }
            label={t("scaleWeight")}
          />
        )}
      </Stack>
      <Stack spacing={2} direction="row" mt={2} flex={1} px={1}>
        <HeatDropTimeline
          start={loadingStart}
          end={loadingEnd}
          drops={heatData.drops}
          dropBreakdown={dropBreakdown}
          scaleWeightSeries={
            dropBreakdown === "mass" &&
            showScaleWeight &&
            scaleWeightMatchesGrouping
              ? scaleWeights.series.filter((pt) =>
                  relevantScaleAreas.has(pt.area)
                )
              : undefined
          }
          dropGrouping={dropGrouping}
        />
        <HeatDropSummaryTable
          drops={heatData.drops}
          materials={materialNames}
        />
      </Stack>
    </>
  );
};
